/**
 * Spreadsheet Preview Panel.
 * Displays spreadsheet plan before creation.
 */

import React from 'react';
import {
  Card,
  CardHeader,
  CardPreview,
  Text,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import { CheckmarkCircleRegular, ArrowRightRegular } from '@fluentui/react-icons';
import type { SpreadsheetPlan } from '@excel-ai-studio/shared';
import './SpreadsheetPreviewPanel.css';

interface SpreadsheetPreviewPanelProps {
  readonly plan: SpreadsheetPlan;
  readonly onGenerate?: () => void;
  readonly isGenerating?: boolean;
}

export const SpreadsheetPreviewPanel: React.FC<SpreadsheetPreviewPanelProps> = ({
  plan,
  onGenerate,
  isGenerating,
}) => {
  return (
    <div className="spreadsheet-preview-panel">
      {/* Header */}
      <Card className="preview-header-card">
        <CardHeader
          header={
            <div className="preview-header">
              <div className="preview-title">
                <Text weight="semibold" size={500}>
                  {plan.sheetName}
                </Text>
                <Text size={300}>
                  {plan.description}
                </Text>
              </div>
              <Badge appearance="outline" size="large">
                {Math.round(plan.confidence * 100)}% confiance
              </Badge>
            </div>
          }
        />
      </Card>

      {/* Columns */}
      <Card className="preview-section-card">
        <CardHeader
          header={
            <Text weight="semibold" size={400}>
              Colonnes ({plan.columns.length})
            </Text>
          }
        />
        <CardPreview>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Nom</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Largeur</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plan.columns.map((col: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>{col.name}</TableCell>
                  <TableCell>
                    <Badge appearance="outline" size="small">
                      {col.type}
                    </Badge>
                  </TableCell>
                  <TableCell>{col.width || 'Auto'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardPreview>
      </Card>

      {/* Formulas */}
      {plan.formulas.length > 0 && (
        <Card className="preview-section-card">
          <CardHeader
            header={
              <Text weight="semibold" size={400}>
                Formules ({plan.formulas.length})
              </Text>
            }
          />
          <CardPreview>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Colonne</TableHeaderCell>
                  <TableHeaderCell>Formule</TableHeaderCell>
                  <TableHeaderCell>Description</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.formulas.map((f: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell>{f.column}</TableCell>
                    <TableCell>
                      <Text size={300}>{f.formula}</Text>
                    </TableCell>
                    <TableCell>{f.description || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardPreview>
        </Card>
      )}

      {/* Validations */}
      {plan.validations.length > 0 && (
        <Card className="preview-section-card">
          <CardHeader
            header={
              <Text weight="semibold" size={400}>
                Validations ({plan.validations.length})
              </Text>
            }
          />
          <CardPreview>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Colonne</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  <TableHeaderCell>Détails</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.validations.map((v: any, idx: number) => (
                  <TableRow key={idx}>
                    <TableCell>{v.column}</TableCell>
                    <TableCell>
                      <Badge appearance="outline" size="small">
                        {v.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {v.values ? v.values.join(', ') : v.min !== undefined ? `${v.min}-${v.max}` : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardPreview>
        </Card>
      )}

      {/* Sample Data */}
      {plan.sampleData && plan.sampleData.length > 0 && (
        <Card className="preview-section-card">
          <CardHeader
            header={
              <Text weight="semibold" size={400}>
                Données d'exemple
              </Text>
            }
          />
          <CardPreview>
            <Table>
              <TableHeader>
                <TableRow>
                  {plan.columns.map((col: any, idx: number) => (
                    <TableHeaderCell key={idx}>{col.name}</TableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {plan.sampleData.map((row: any, ridx: number) => (
                  <TableRow key={ridx}>
                    {row.map((cell: any, cidx: number) => (
                      <TableCell key={cidx}>{String(cell)}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardPreview>
        </Card>
      )}

      {/* Suggested Charts */}
      {plan.suggestedCharts && plan.suggestedCharts.length > 0 && (
        <Card className="preview-section-card">
          <CardHeader
            header={
              <Text weight="semibold" size={400}>
                Graphiques suggérés
              </Text>
            }
          />
          <CardPreview>
            <div className="charts-list">
              {plan.suggestedCharts.map((chart: any, idx: number) => (
                <div key={idx} className="chart-item">
                  <CheckmarkCircleRegular />
                  <Text size={300}>{chart.title} ({chart.type})</Text>
                </div>
              ))}
            </div>
          </CardPreview>
        </Card>
      )}

      {/* Action Button */}
      <div className="preview-actions">
        <Button
          appearance="primary"
          size="large"
          icon={<ArrowRightRegular />}
          onClick={onGenerate}
          disabled={isGenerating}
        >
          {isGenerating ? 'Création en cours...' : 'Créer la feuille'}
        </Button>
      </div>
    </div>
  );
};
